import AdminLayout from "@/components/AdminLayout";
import { PageHeader } from "@/components/ui-system";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { ShoppingBag, KeyRound, RefreshCw, Settings, AlertTriangle, MapPinned, Code2, Send, Link2 } from "lucide-react";

const Section = ({ id, icon: Icon, title, children }) => (
  <Card className="border-0 shadow-sm scroll-mt-24" id={id}>
    <CardContent className="pt-6">
      <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-800 mb-3">
        {Icon && <Icon className="w-5 h-5 text-slate-700" />} {title}
      </h2>
      <div className="space-y-3 text-sm text-slate-600 leading-relaxed">{children}</div>
    </CardContent>
  </Card>
);
const Code = ({ children }) => (
  <pre className="bg-slate-900 text-slate-100 rounded-lg p-3 text-xs overflow-x-auto font-mono whitespace-pre">{children}</pre>
);
const Mono = ({ children }) => <code className="font-mono text-[13px] bg-slate-100 text-slate-800 rounded px-1 py-0.5">{children}</code>;
const TH = ({ children }) => <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600 border-b">{children}</th>;
const TD = ({ children, mono }) => <td className={`px-3 py-2 align-top border-b border-slate-100 ${mono ? "font-mono text-[13px]" : ""}`}>{children}</td>;

const TOC = [
  ["overview", "Overview"], ["connect", "Create the channel"], ["architecture", "Architecture"],
  ["endpoint", "Push endpoint & auth"], ["schema", "Order payload"], ["responses", "Responses & dedupe"],
  ["sample", "Sample request"], ["config", "Configuration"], ["limits", "Limits & behaviour"],
  ["troubleshooting", "Troubleshooting"], ["roadmap", "Roadmap"], ["related", "Related guides"],
];

const CustomApiDevDocs = ({ user, onLogout }) => (
  <AdminLayout user={user} onLogout={onLogout}>
    <div className="space-y-6 w-full max-w-4xl">
      <PageHeader
        title="Custom / API Integration — Developer Guide"
        subtitle="Push orders into B2C from any system via a simple authenticated API"
        badge={<Badge variant="outline" className="border-slate-300 text-slate-700">B2C · Channels</Badge>}
      />

      <Card className="border-0 shadow-sm">
        <CardContent className="pt-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">On this page</p>
          <div className="flex flex-wrap gap-2">
            {TOC.map(([id, label]) => (
              <a key={id} href={`#${id}`} className="text-xs rounded-full bg-slate-100 hover:bg-slate-200 px-2.5 py-1 text-slate-600 transition-colors">{label}</a>
            ))}
          </div>
        </CardContent>
      </Card>

      <Section id="overview" icon={ShoppingBag} title="1. Overview">
        <p>The <strong>Custom / API</strong> channel is a <strong>push</strong> integration: instead of EdgeLMS pulling from a store, the seller's own system (ERP, marketplace, custom storefront, no-code tool) <strong>POSTs each order</strong> to a key-authenticated EdgeLMS endpoint. Each pushed order appears in <Mono>B2C Orders</Mono> as <Mono>NEW</Mono>, ready to ship.</p>
        <p>No polling, no webhooks, no store credentials — the seller controls exactly when an order is sent.</p>
      </Section>

      <Section id="connect" icon={KeyRound} title="2. Create the channel (mint the key)">
        <ol className="list-decimal list-inside space-y-1.5">
          <li>Seller → <strong>B2C → Sales Channels → Connect a channel → Custom / API</strong>.</li>
          <li>Enter a name, pick a default pickup location + default package, Connect.</li>
          <li>EdgeLMS shows the <strong>API key</strong> (<Mono>edgk_…</Mono>) and the <strong>orders endpoint</strong> — <strong>once</strong>. Copy both.</li>
        </ol>
        <p className="text-slate-500">The key is stored <strong>encrypted</strong> and never shown again. If lost, disconnect the channel and reconnect for a new one.</p>
      </Section>

      <Section id="architecture" icon={Code2} title="3. Architecture">
        <ul className="list-disc list-inside space-y-1.5">
          <li><strong>Collection</strong> <Mono>b2c_channels</Mono> — one doc per push channel (<Mono>EDGCH-</Mono> display id, EdgeLMS's channel id), <Mono>type: "custom"</Mono>. Holds the <strong>encrypted API key</strong>, <Mono>default_pickup_location_id</Mono>, <Mono>default_package</Mono>, <Mono>sku_weights</Mono>, stats. No <Mono>sync_cursor</Mono> / <Mono>webhook_id</Mono> (nothing is polled or registered).</li>
          <li><strong>Encryption</strong> — <Mono>utils/secret_box.py</Mono> (Fernet — symmetric authenticated encryption — keyed from <Mono>B2C_CHANNEL_SECRET_KEY</Mono> or <Mono>JWT_SECRET_KEY</Mono>). The <Mono>edgk_…</Mono> key is stored as ciphertext; the push request's key is compared with a constant-time check.</li>
          <li><strong>Ingest route</strong> — <Mono>routes/b2c/custom_ingest.py</Mono> (the public, key-gated <Mono>POST …/orders</Mono> endpoint).</li>
          <li><strong>Adapter</strong> — <Mono>services/b2c/channels/custom_adapter.py</Mono> (<Mono>normalize_order</Mono> — a pass-through that maps your payload to the internal order shape, honouring an explicit <Mono>package</Mono>).</li>
          <li><strong>Service</strong> — <Mono>services/b2c/channel_service.py</Mono> (<Mono>connect_custom</Mono> mints the key; <Mono>handle_custom_order</Mono> authenticates + dedupes; <Mono>ingest_order</Mono> writes the order). Request model <Mono>models/b2c_channel.py</Mono> (<Mono>CustomConnectRequest</Mono>, <Mono>CHANNEL_CATALOG</Mono>).</li>
          <li><strong>Ingestion</strong> → a <Mono>b2c_orders</Mono> doc at <Mono>NEW</Mono>, <strong>deduped</strong> on <Mono>(seller_id, channel_id, external_order_id)</Mono> (unique index + pre-check).</li>
        </ul>
      </Section>

      <Section id="endpoint" icon={Send} title="4. Push endpoint & authentication">
        <Code>{`POST  {API_BASE}/b2c/channels/custom/{channel_id}/orders
Header  X-EdgeLMS-Channel-Key: edgk_…
Header  Content-Type: application/json`}</Code>
        <p>The channel id is in the endpoint shown at connect time. A missing or wrong key (or unknown channel) returns <Mono>401</Mono> — never silently succeeds.</p>
      </Section>

      <Section id="schema" icon={Code2} title="5. Order payload">
        <div className="overflow-x-auto">
          <table className="w-full text-sm border border-slate-100 rounded">
            <thead className="bg-slate-50"><tr><TH>Field</TH><TH>Required</TH><TH>Notes</TH></tr></thead>
            <tbody>
              <tr><TD mono>order_id</TD><TD>yes</TD><TD>Your unique order id — the <strong>dedupe key</strong> (a repeat is ignored)</TD></tr>
              <tr><TD mono>order_number</TD><TD>no</TD><TD>Display reference shown to the seller</TD></tr>
              <tr><TD mono>payment_mode</TD><TD>no</TD><TD><Mono>cod</Mono> (Cash on Delivery) or <Mono>prepaid</Mono> (default prepaid)</TD></tr>
              <tr><TD mono>buyer</TD><TD>yes</TD><TD><Mono>&#123; name, phone, pincode, address, city, state, email?, country? &#125;</Mono> — phone &amp; pincode should be valid Indian values for shipping</TD></tr>
              <tr><TD mono>products[]</TD><TD>yes</TD><TD><Mono>&#123; name, sku, unit_price, qty &#125;</Mono></TD></tr>
              <tr><TD mono>order_value</TD><TD>no</TD><TD>Goods value; if omitted, summed from products</TD></tr>
              <tr><TD mono>cod_amount</TD><TD>no</TD><TD>COD amount to collect; defaults to order_value for COD</TD></tr>
              <tr><TD mono>package</TD><TD>no</TD><TD><Mono>&#123; dead_weight_kg, length_cm, breadth_cm, height_cm &#125;</Mono>; if omitted, the channel default package is used</TD></tr>
            </tbody>
          </table>
        </div>
      </Section>

      <Section id="responses" icon={RefreshCw} title="6. Responses & dedupe">
        <ul className="list-disc list-inside space-y-1.5">
          <li><strong>201/200</strong> <Mono>&#123; "ok": true, "created": true, "order_number": "EDGB-…" &#125;</Mono> — a new B2C order was created.</li>
          <li><strong>200</strong> <Mono>&#123; "ok": true, "created": false, "duplicate": true &#125;</Mono> — this <Mono>order_id</Mono> was already pushed (idempotent; safe to retry).</li>
          <li><strong>401</strong> — bad/missing key or unknown channel.</li>
          <li><strong>422</strong> — missing <Mono>order_id</Mono>.</li>
        </ul>
        <p>Because the response is idempotent on <Mono>order_id</Mono>, you can safely retry on a network error without creating duplicates.</p>
      </Section>

      <Section id="sample" icon={Code2} title="7. Sample request">
        <Code>{`curl -X POST '{API_BASE}/b2c/channels/custom/{channel_id}/orders' \\
  -H 'X-EdgeLMS-Channel-Key: edgk_YOURKEY' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "order_id": "SO-10231",
    "payment_mode": "cod",
    "buyer": {
      "name": "Asha Rao", "phone": "9876543210", "pincode": "560001",
      "address": "12 MG Road", "city": "Bengaluru", "state": "Karnataka"
    },
    "products": [
      { "name": "Cotton Tee", "sku": "TEE-BLUE-M", "unit_price": 499, "qty": 1 }
    ],
    "cod_amount": 499
  }'`}</Code>
      </Section>

      <Section id="config" icon={Settings} title="8. Configuration (environment)">
        <div className="overflow-x-auto">
          <table className="w-full text-sm border border-slate-100 rounded">
            <thead className="bg-slate-50"><tr><TH>Env var</TH><TH>Purpose</TH><TH>Default</TH></tr></thead>
            <tbody>
              <tr><TD mono>B2C_CHANNEL_SECRET_KEY</TD><TD>Key for encrypting the channel's API key at rest.</TD><TD mono>JWT_SECRET_KEY</TD></tr>
            </tbody>
          </table>
        </div>
        <p className="text-slate-500"><Mono>PUBLIC_API_BASE_URL</Mono> / <Mono>B2C_CHANNEL_SYNC_MINUTES</Mono> are not used by Custom (no webhooks, no polling).</p>
      </Section>

      <Section id="limits" icon={AlertTriangle} title="9. Limits & behaviour">
        <ul className="list-disc list-inside space-y-1.5">
          <li><strong>One order per request</strong> — POST each order individually.</li>
          <li><strong>Idempotent</strong> on <Mono>order_id</Mono> — duplicates are ignored, not errored.</li>
          <li><strong>One-way</strong> — EdgeLMS does not push fulfillment/AWB back to your system (v1).</li>
          <li><strong>India-focused</strong> — buyer phone is reduced to its last 10 digits, pincode treated as Indian. A non-Indian / invalid pincode still imports as <Mono>NEW</Mono> with <Mono>zone = null</Mono>.</li>
          <li><MapPinned className="inline w-4 h-4 text-slate-400 mr-1" /><strong>Unserviceable / invalid pincode</strong> — the order imports as NEW but <strong>ship is blocked with a clear 422</strong>; the seller fixes the address, then ships.</li>
        </ul>
      </Section>

      <Section id="troubleshooting" icon={AlertTriangle} title="10. Troubleshooting">
        <ul className="list-disc list-inside space-y-1.5">
          <li><strong>401 on every request</strong> — wrong key/endpoint, or the channel was disconnected. Reconnect to mint a fresh key.</li>
          <li><strong>Order didn't appear</strong> — a <Mono>duplicate:true</Mono> response means that <Mono>order_id</Mono> was already imported.</li>
          <li><strong>Wrong COD amount</strong> — set <Mono>cod_amount</Mono> explicitly; otherwise it defaults to the goods value.</li>
          <li><strong>Can't ship a pushed order</strong> — a 422 about pincode/weight means the buyer address or package needs fixing on that order first.</li>
        </ul>
      </Section>

      <Section id="roadmap" icon={RefreshCw} title="11. Roadmap">
        <p>Deliberately <strong>not</strong> built in v1, framed as next so missing pieces read as roadmap, not bugs:</p>
        <ul className="list-disc list-inside space-y-1.5">
          <li><strong>One-way only</strong> — v2 will write fulfillment / AWB (Air Waybill / tracking number) status back to your system.</li>
          <li><strong>One order per request</strong> — a batch endpoint (many orders per POST) is planned.</li>
          <li><strong>No delivery callbacks</strong> — push-only today; outbound webhooks for status updates are a later phase.</li>
        </ul>
      </Section>

      <Section id="related" icon={Link2} title="12. Related guides">
        <p>Sibling channel integration guides (same connector foundation):</p>
        <ul className="list-disc list-inside space-y-1.5">
          <li><Link className="text-slate-700 underline" to="/admin/dev-docs/shopify">Shopify Integration</Link></li>
          <li><Link className="text-slate-700 underline" to="/admin/dev-docs/woocommerce">WooCommerce Integration</Link></li>
          <li><Link className="text-slate-700 underline" to="/admin/dev-docs/magento">Magento Integration</Link></li>
        </ul>
        <p>The seller-facing <strong>connect guide</strong> (shown on <Mono>Sales Channels → Connect → Custom / API</Mono>) is defined in <Mono>models/b2c_channel.py → CHANNEL_CATALOG</Mono>. Keep its steps in sync with §2 above.</p>
      </Section>
    </div>
  </AdminLayout>
);

export default CustomApiDevDocs;
